package components.project

import components.form.commentSection
import components.project.ServerProjectPreviewsTable.TableColumnId
import components.project.ServerProjectPreviewsTable.getCustomerColumn
import components.project.ServerProjectPreviewsTable.getEditorColumn
import components.project.ServerProjectPreviewsTable.getRelevantProcessStateColumn
import components.project.ServerProjectPreviewsTable.projectDetails
import it.neckar.commons.kotlin.js.LocalStorageKeyPrefix
import it.neckar.lizergy.model.company.CompanyResolver
import it.neckar.lizergy.model.project.previews.ProjectQueryComponent
import it.neckar.lizergy.model.project.previews.ProjectQueryForUser
import it.neckar.lizergy.model.project.previews.ServerProjectPreview
import it.neckar.lizergy.model.project.process.state.AdvanceInvoiceProcessStateEntry.AdvanceInvoiceProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyBasementPreparationProcessStateEntry.AssemblyBasementPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyPortfolioProcessStateEntry.AssemblyPortfolioProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyRoofPreparationProcessStateEntry.AssemblyRoofPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.GridAssessmentProcessStateEntry.GridAssessmentProcessStates
import it.neckar.lizergy.model.project.process.state.OrderSpecialMaterialProcessStateEntry.OrderSpecialMaterialProcessStates
import it.neckar.lizergy.model.project.process.state.ProjectProcessStateEntry.ProjectProcessStates
import it.neckar.react.common.form.*
import it.neckar.react.common.table.*
import react.*
import react.dom.*
import router.useDocumentTitle
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useSelectCompanyResolver
import store.hooks.useSelectUserResolver

val OrderSpecialMaterialsOverviewFromUrl: FC<Props> = fc("OrderSpecialMaterialsOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = OrderSpecialMaterialProcessStates.entries

  useDocumentTitle(companyName, "Sondermaterial Bestellungen")

  h1("mb-3") { +"Sondermaterial Bestellungen" }

  ProjectsOverview {
      attrs {
        this.processStatesToFilter = relevantProcessStates
        this.processStatesToHide = listOf(OrderSpecialMaterialProcessStates.Done, ProjectProcessStates.Archived, ProjectProcessStates.Paused)
        this.projectQueryComponent = ProjectQueryComponent.OrderSpecialMaterial
        this.tableColumns = { ServerProjectPreviewsTable.defaultColumns(projectQueryComponent = projectQueryComponent, userResolver = userResolver, companyResolver = companyResolver) }
        this.sortedBy = SortedBy.Unsorted
        this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
        this.includeArchived = false
        this.keyPrefix = LocalStorageKeyPrefix("OrderSpecialMaterials")
      }
    }
}

val GridAssessmentsOverviewFromUrl: FC<Props> = fc("GridAssessmentsOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = GridAssessmentProcessStates.entries

  useDocumentTitle(companyName, "Netzvoranfragen")

  h1("mb-3") { +"Netzvoranfragen" }

  ProjectsOverview {
      attrs {
        this.processStatesToFilter = relevantProcessStates
        this.processStatesToHide = listOf(GridAssessmentProcessStates.Accepted, ProjectProcessStates.Archived, ProjectProcessStates.Paused)
        this.projectQueryComponent = ProjectQueryComponent.GridAssessment
        this.tableColumns = { ServerProjectPreviewsTable.defaultColumns(projectQueryComponent = projectQueryComponent, userResolver = userResolver, companyResolver = companyResolver) }
        this.sortedBy = SortedBy.Unsorted
        this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
        this.includeArchived = false
        this.keyPrefix = LocalStorageKeyPrefix("GridAssessments")
      }
  }
}

val AssemblyPortfoliosOverviewFromUrl: FC<Props> = fc("AssemblyPortfoliosOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = AssemblyPortfolioProcessStates.entries

  useDocumentTitle(companyName, "Montagemappen erstellen")

  h1("mb-3") { +"Montagemappen erstellen" }

  ProjectsOverview {
      attrs {
        this.processStatesToFilter = relevantProcessStates
        this.processStatesToHide = listOf(AssemblyPortfolioProcessStates.Accepted, ProjectProcessStates.Archived, ProjectProcessStates.Paused)
        this.projectQueryComponent = ProjectQueryComponent.AssemblyPortfolio
        this.tableColumns = { ServerProjectPreviewsTable.defaultColumns(projectQueryComponent = projectQueryComponent, userResolver = userResolver, companyResolver = companyResolver) }
        this.sortedBy = SortedBy.Unsorted
        this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
        this.includeArchived = false
        this.keyPrefix = LocalStorageKeyPrefix("AssemblyPortfolios")
      }
  }
}

val ScheduleAssembliesOverviewFromUrl: FC<Props> = fc("ScheduleAssembliesOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = listOf(AssemblyRoofPreparationProcessStates.Missing, AssemblyBasementPreparationProcessStates.Missing)

  useDocumentTitle(companyName, "Montagetermine einplanen")

  h1("mb-3") { +"Montagetermine einplanen" }

  ProjectsOverview {
      attrs {
        this.processStatesToFilter = relevantProcessStates
        this.processStatesToHide = listOf(ProjectProcessStates.Archived, ProjectProcessStates.Paused)
        this.projectQueryComponent = ProjectQueryComponent.ScheduleAssembly
        this.tableColumns = { ServerProjectPreviewsTable.defaultColumns(projectQueryComponent = projectQueryComponent, userResolver = userResolver, companyResolver = companyResolver) }
        this.sortedBy = SortedBy.Unsorted
        this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
        this.includeArchived = false
        this.keyPrefix = LocalStorageKeyPrefix("ScheduleAssemblies")
      }
  }
}

val AdvanceInvoicesOverviewFromUrl: FC<Props> = fc("AdvanceInvoicesOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = AdvanceInvoiceProcessStates.entries

  useDocumentTitle(companyName, "Anzahlungsrechnungen")

  h1("mb-3") { +"Anzahlungsrechnungen" }

  ProjectsOverview {
    attrs {
      this.processStatesToFilter = relevantProcessStates
      this.processStatesToHide = listOf(AdvanceInvoiceProcessStates.Paid, ProjectProcessStates.Archived, ProjectProcessStates.Paused)
      this.projectQueryComponent = ProjectQueryComponent.AdvanceInvoice
      this.tableColumns = {
        buildList {
          add(getProjectColumnWithAdvanceInvoiceComments(companyResolver))
          add(getCustomerColumn())
          add(getEditorColumn(projectQueryComponent, userResolver))
          add(getRelevantProcessStateColumn(projectQueryComponent))
        }
      }
      this.sortedBy = SortedBy.Unsorted
      this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
      this.includeArchived = false
      this.keyPrefix = LocalStorageKeyPrefix("AdvanceInvoices")
    }
  }
}

fun <T : ServerProjectPreview> getProjectColumnWithAdvanceInvoiceComments(companyResolver: CompanyResolver): TableDataColumn<T> {
  return TableDataColumn(
    id = TableColumnId.Project.id,
    title = "Projekt",
    sortFunction = compareBy { it.displayName },
  ) { projectPreview ->
    {
      projectDetails(projectPreview, companyResolver)
      div("my-3") {
        commentSection(projectPreview.advanceInvoiceId, editableStatus = EditableStatus.ReadOnly)
      }
    }
  }
}
